import React, { useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import { useTranslation } from 'react-i18next';
import './forecast.css';
import { MONTH_FULL_NAMES, numKeyDown } from './forecastUtils';

export interface ForecastSetupValues {
    name: string;
    description: string;
    start_year: number;
    start_month: number;
    year_type: 'calendar' | 'fiscal';
    forecast_type: ForecastType;
    opening_cash: number;
}

export type ForecastType = 'new_business' | 'new_location';

interface Props {
    show: boolean;
    title: string;
    initial?: Partial<ForecastSetupValues>;
    saving: boolean;
    onClose: () => void;
    onSave: (values: ForecastSetupValues) => void;
}

const thisYear = new Date().getFullYear();
const DEFAULTS: ForecastSetupValues = {
    name: '',
    description: '',
    start_year: thisYear + 1,
    start_month: 1,
    year_type: 'calendar',
    forecast_type: 'new_business',
    opening_cash: 0,
};

/**
 * Create / edit the forecast header: type (new business / new location), name,
 * start, calendar vs fiscal, optional starting cash (shown as Opening Equity).
 * Other funding (loans, owner capital, SAFE) is entered under Cash & Financing.
 */
const ForecastSetupModal: React.FC<Props> = ({ show, title, initial, saving, onClose, onSave }) => {
    const { t } = useTranslation('scenes.FiveYearForecast');
    const [values, setValues] = useState<ForecastSetupValues>({ ...DEFAULTS, ...initial });
    const [cashText, setCashText] = useState<string>(initial?.opening_cash ? String(initial.opening_cash) : '');
    const [error, setError] = useState<string>('');

    useEffect(() => {
        if (show) {
            setValues({ ...DEFAULTS, ...initial });
            setCashText(initial?.opening_cash ? String(initial.opening_cash) : '');
            setError('');
        }
    }, [show]); // eslint-disable-line react-hooks/exhaustive-deps

    const set = (key: keyof ForecastSetupValues, value: any) => setValues((v) => ({ ...v, [key]: value }));

    const submit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!values.name.trim()) {
            setError(t('setup.nameRequired', 'Enter a forecast name.'));
            return;
        }
        const trimmedCash = cashText.trim();
        const openingCash = trimmedCash === '' ? 0 : Number(trimmedCash);
        if (trimmedCash !== '' && (!Number.isFinite(openingCash) || openingCash < 0)) {
            setError(t('setup.invalidStartingCash', 'Enter a valid number for starting cash.'));
            return;
        }
        // Forecast.opening_cash is DECIMAL(18,2); JS can only safely
        // represent integers up to Number.MAX_SAFE_INTEGER (~15 digits).
        if (!Number.isSafeInteger(openingCash)) {
            setError(t('setup.startingCashTooLarge', 'Starting cash is too large - it must be 15 digits or fewer.'));
            return;
        }
        onSave({ ...values, name: values.name.trim(), opening_cash: openingCash });
    };

    const years = Array.from({ length: 8 }, (_, i) => thisYear - 2 + i);

    return (
        <Modal show={show} onHide={onClose} centered className="fyf-modal">
            <Form onSubmit={submit} noValidate>
                <Modal.Header closeButton>
                    <Modal.Title className="h6 mb-0">{title}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label>{t('setup.forecastType', 'This forecast is for')}</Form.Label>
                        <div className="d-flex gap-4">
                            <Form.Check type="radio" id="fy-new-business" name="forecast_type"
                                label={t('type.new_business', 'New business')}
                                checked={values.forecast_type === 'new_business'}
                                onChange={() => set('forecast_type', 'new_business')} />
                            <Form.Check type="radio" id="fy-new-location" name="forecast_type"
                                label={t('type.new_location', 'New location')}
                                checked={values.forecast_type === 'new_location'}
                                onChange={() => set('forecast_type', 'new_location')} />
                        </div>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>{t('setup.name', 'Forecast name')}</Form.Label>
                        <Form.Control
                            value={values.name}
                            maxLength={255}
                            placeholder={t('setup.namePlaceholder', 'e.g. Dallas location – 2027 plan')}
                            onChange={(e) => set('name', e.target.value)}
                            autoFocus
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>{t('setup.description', 'Description (optional)')}</Form.Label>
                        <Form.Control as="textarea" rows={2} maxLength={1000} value={values.description}
                            onChange={(e) => set('description', e.target.value)} />
                    </Form.Group>
                    <div className="row g-3 mb-3">
                        <div className="col-6">
                            <Form.Label>{t('setup.startMonth', 'Start month')}</Form.Label>
                            <Form.Select value={values.start_month} onChange={(e) => set('start_month', Number(e.target.value))}>
                                {MONTH_FULL_NAMES.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                            </Form.Select>
                        </div>
                        <div className="col-6">
                            <Form.Label>{t('setup.startYear', 'Start year')}</Form.Label>
                            <Form.Select value={values.start_year} onChange={(e) => set('start_year', Number(e.target.value))}>
                                {years.map((y) => <option key={y} value={y}>{y}</option>)}
                            </Form.Select>
                        </div>
                    </div>
                    <Form.Group className="mb-3">
                        <Form.Label>{t('setup.yearType', 'Year type')}</Form.Label>
                        <div className="d-flex gap-4">
                            <Form.Check type="radio" id="fy-calendar" name="year_type" label={t('setup.calendar', 'Calendar year')}
                                checked={values.year_type === 'calendar'} onChange={() => set('year_type', 'calendar')} />
                            <Form.Check type="radio" id="fy-fiscal" name="year_type" label={t('setup.fiscal', 'Fiscal year')}
                                checked={values.year_type === 'fiscal'} onChange={() => set('year_type', 'fiscal')} />
                        </div>
                        <Form.Text muted>
                            {t('setup.yearTypeHint', 'Each forecast year runs 12 months from the start month (e.g. Sep–Aug).')}
                        </Form.Text>
                    </Form.Group>
                    <Form.Group className="mb-2">
                        <Form.Label>{t('setup.startingCash', 'Starting cash (optional)')}</Form.Label>
                        <div className="input-group">
                            <span className="input-group-text">$</span>
                            <Form.Control type="text" inputMode="numeric" value={cashText}
                                placeholder="0" maxLength={16} onKeyDown={numKeyDown} onChange={(e) => setCashText(e.target.value.replace(/\D/g, ''))} />
                        </div>
                        <Form.Text muted>
                            {t('setup.startingCashHint', 'Cash already in the bank on day one (Month 1 "Cash on Hand - Prior Period"). It shows as Opening Equity on the balance sheet. Enter loans, owner capital and SAFE under Cash & Financing.')}
                        </Form.Text>
                    </Form.Group>
                    {error && <div className="text-danger small mt-2">{error}</div>}
                </Modal.Body>
                <Modal.Footer>
                    <button type="button" className="fyf-secondary" onClick={onClose}>
                        {t('common.cancel', 'Cancel')}
                    </button>
                    <button type="submit" className="fyf-primary" disabled={saving}>
                        {saving ? <Spinner animation="border" size="sm" /> : t('common.save', 'Save')}
                    </button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default ForecastSetupModal;
